import { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { manageAISharing } from '../services/aiSharingService';
import {
  ArrowLeft, Bell, Download, LogOut,
  ChevronRight, Edit3, Shield, Calendar, BookOpen, FileText,
  Star, Sun, Moon, User, Briefcase, Building, AtSign,
  Linkedin, X, Check, Camera, QrCode, Sparkles,
  Brain, MessageSquare, Info, Scale, Eye, EyeOff, Trash2,
  KeyRound, type LucideIcon,
} from 'lucide-react';
import BottomNav from '../components/BottomNav';
import { DeleteAccountConfirm } from '../components/DeleteAccountConfirm';
import { useApp } from '../context/AppContext';
import { useFavorites } from '../hooks/useFavorites';
import { notify } from '../utils/notifications';
import { AvatarImage } from '../components/AvatarImage';
import { apiClient } from '../services/apiClient';
import { eventService } from '../services/dataService';
import {
  directoryPrivacyService,
  type DirectoryPreference,
} from '../services/directoryPrivacyService';
import { contactQrDataUrl, buildVCard } from '../utils/contact';
import { exportAccountFile, shareText } from '../utils/exportShare';
import type { ConferenceEvent } from '@conference/shared';
import { APP_VERSION, SUPPORT_EMAIL } from '../config/publicResources';
import { openSupportEmail } from '../utils/supportEmail';
import { useEventModules } from '../hooks/useEventModules';

/* ─── Toggle Switch ─────────────────────────────────────────── */
function Toggle({
  value,
  disabled = false,
  onChange,
}: {
  value: boolean;
  disabled?: boolean;
  onChange: (e: React.MouseEvent) => void;
}) {
  return (
    <button
      onClick={onChange}
      disabled={disabled}
      aria-label={value ? 'Enabled' : 'Disabled'}
      className="relative w-11 h-6 rounded-full transition-colors duration-200 shrink-0 disabled:opacity-60"
      style={{ backgroundColor: value ? 'var(--app-accent-solid)' : 'var(--app-muted)' }}
    >
      <div
        className="absolute top-1 size-4 rounded-full bg-white shadow-sm transition-transform duration-200"
        style={{ transform: value ? 'translateX(22px)' : 'translateX(4px)' }}
      />
    </button>
  );
}

/* ─── Section Header ────────────────────────────────────────── */
function SectionLabel({ label }: { label: string }) {
  return (
    <p
      className="text-xs tracking-widest uppercase px-4 mb-2 mt-1"
      style={{ color: 'var(--app-text-muted)' }}
    >
      {label}
    </p>
  );
}

/* ─── Settings Row ──────────────────────────────────────────── */
function SettingRow({
  icon: Icon, label, subtitle, isToggle, value, disabled, color, onPress,
}: {
  icon: LucideIcon;
  label: string;
  subtitle?: string;
  isToggle?: boolean;
  value?: boolean;
  disabled?: boolean;
  color?: string;
  onPress: () => void;
}) {
  const accentColor = color || 'var(--app-accent)';
  const iconBg = color ? `${color}20` : 'var(--app-accent-dim)';
  const Tag = isToggle ? 'div' : 'button';
  return (
    <Tag
      onClick={disabled ? undefined : onPress}
      className="w-full flex items-center gap-3 px-4 py-3.5 transition-colors active:opacity-70"
    >
      <div className="size-9 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: iconBg }}>
        <Icon className="w-4 h-4" style={{ color: accentColor }} />
      </div>
      <div className="flex-1 text-left min-w-0">
        <p className="text-sm" style={{ color: 'var(--app-text)' }}>{label}</p>
        {subtitle && <p className="text-xs mt-0.5" style={{ color: 'var(--app-text-muted)' }}>{subtitle}</p>}
      </div>
      {isToggle ? (
        <Toggle
          value={!!value}
          disabled={disabled}
          onChange={(e) => { e.stopPropagation(); if (!disabled) onPress(); }}
        />
      ) : (
        <ChevronRight className="w-4 h-4 shrink-0" style={{ color: 'var(--app-text-subtle)' }} />
      )}
    </Tag>
  );
}

/* ─── Divider ───────────────────────────────────────────────── */
function Divider() {
  return <div style={{ borderTop: '1px solid var(--app-divider)' }} />;
}

function ProfileAvatar({
  src,
  name,
  alt,
  className,
}: {
  src?: string;
  name: string;
  alt: string;
  className: string;
}) {
  return <AvatarImage source={src} alt={alt} className={className} fallback={
    <div
      role="img"
      aria-label={alt}
      className={`${className} flex items-center justify-center text-2xl font-semibold`}
      style={{ backgroundColor: 'var(--app-accent-dim)', color: 'var(--app-accent)' }}
    >
      {name.trim().charAt(0).toUpperCase() || '?'}
    </div>
  } />;
}

/* ─── QR Badge Modal ────────────────────────────────────────── */
function QRBadgeModal({ user, onClose }: {
  user: { id?: string; name: string; title?: string; company?: string; email?: string; linkedin?: string };
  onClose: () => void;
}) {
  const [qr, setQr] = useState<string | null>(null);
  const [qrError, setQrError] = useState(false);

  const card = {
    name: user.name,
    title: user.title,
    company: user.company,
    email: user.email,
    linkedin: user.linkedin,
  };

  useEffect(() => {
    let cancelled = false;
    contactQrDataUrl(card)
      .then((url) => { if (!cancelled) setQr(url); })
      .catch(() => { if (!cancelled) setQrError(true); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.name, user.title, user.company, user.email, user.linkedin]);

  const handleShareContact = async () => {
    try {
      const ok = await shareText({
        title: `${user.name} — contact`,
        text: buildVCard(card),
        dialogTitle: 'Share my contact',
      });
      if (ok) notify.success('Contact shared');
    } catch {
      notify.error('Could not share contact');
    }
  };

  return (
    <div className="absolute inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
      <div
        className="mx-6 w-full max-w-xs rounded-2xl p-6 border flex flex-col items-center gap-4"
        style={{ backgroundColor: 'var(--app-surface)', borderColor: 'var(--app-border)' }}
      >
        <div className="flex items-center justify-between w-full">
          <span className="text-sm" style={{ color: 'var(--app-text)' }}>My Conference Badge</span>
          <button onClick={onClose} className="size-8 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--app-muted)' }}>
            <X className="w-4 h-4" style={{ color: 'var(--app-text)' }} />
          </button>
        </div>

        {/* Real scannable QR (encodes a vCard) */}
        <div
          className="size-48 rounded-xl flex items-center justify-center border overflow-hidden"
          style={{ backgroundColor: 'white', borderColor: 'var(--app-border)' }}
        >
          {qr ? (
            <img src={qr} alt="Contact QR code" className="w-full h-full object-contain p-2" />
          ) : qrError ? (
            <QrCode className="w-28 h-28 text-gray-300" />
          ) : (
            <QrCode className="w-28 h-28 text-gray-200 animate-pulse" />
          )}
        </div>

        <div className="text-center">
          <p className="text-sm" style={{ color: 'var(--app-text)' }}>{user.name}</p>
          <p className="text-xs mt-0.5" style={{ color: 'var(--app-text-muted)' }}>{user.title}</p>
          <p className="text-xs" style={{ color: 'var(--app-text-subtle)' }}>{user.company}</p>
        </div>

        <span
          className="text-[10px] px-3 py-1 rounded-full tracking-wide"
          style={{ backgroundColor: 'var(--app-accent-dim)', color: 'var(--app-accent)' }}
        >
          ATTENDEE #{(user?.id ?? 'GUEST').toString().slice(-8).toUpperCase()}
        </span>

        <button
          onClick={handleShareContact}
          className="w-full py-2.5 rounded-xl text-sm active:scale-95 transition-transform"
          style={{ backgroundColor: 'var(--app-accent-solid)', color: '#fff' }}
        >
          Share my contact
        </button>

        <p className="text-[10px] text-center" style={{ color: 'var(--app-text-subtle)' }}>
          Have someone scan this to save your contact, or share it directly
        </p>
      </div>
    </div>
  );
}

/* ─── Sign Out Confirmation ─────────────────────────────────── */
function SignOutConfirm({ onConfirm, onCancel }: { onConfirm: () => void; onCancel: () => void }) {
  return (
    <div className="absolute inset-0 z-50 flex items-end justify-center pb-36" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
      <div
        className="mx-4 w-full max-w-sm rounded-2xl overflow-hidden border"
        style={{ backgroundColor: 'var(--app-surface)', borderColor: 'var(--app-border)' }}
      >
        <div className="p-5 text-center">
          <div
            className="size-12 mx-auto rounded-full flex items-center justify-center mb-3"
            style={{ backgroundColor: 'var(--app-danger-dim)' }}
          >
            <LogOut className="w-5 h-5" style={{ color: 'var(--app-danger)' }} />
          </div>
          <p className="text-sm" style={{ color: 'var(--app-text)' }}>Sign out of your account?</p>
          <p className="text-xs mt-1" style={{ color: 'var(--app-text-muted)' }}>
            Your notes and preferences will be preserved locally.
          </p>
        </div>
        <Divider />
        <div className="flex">
          <button
            onClick={onCancel}
            className="flex-1 py-3.5 text-sm active:opacity-70 transition-opacity"
            style={{ color: 'var(--app-text-muted)' }}
          >
            Cancel
          </button>
          <div className="w-px" style={{ backgroundColor: 'var(--app-divider)' }} />
          <button
            onClick={onConfirm}
            className="flex-1 py-3.5 text-sm active:opacity-70 transition-opacity"
            style={{ color: 'var(--app-danger)' }}
          >
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Edit Personal Info Modal ──────────────────────────────── */
function EditProfileModal({
  user, onSave, onClose, onAvatarChange,
}: {
  user: { name: string; title?: string; company?: string; bio?: string; linkedin?: string; avatar?: string; interests?: string[] };
  onSave: (data: typeof user) => void;
  onClose: () => void;
  onAvatarChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) {
  const [form, setForm] = useState({ ...user });
  const [interestInput, setInterestInput] = useState('');

  function addInterest(tag: string) {
    const trimmed = tag.trim();
    if (!trimmed || (form.interests ?? []).includes(trimmed)) return;
    setForm(f => ({ ...f, interests: [...(f.interests ?? []), trimmed] }));
  }
  function removeInterest(tag: string) {
    setForm(f => ({ ...f, interests: (f.interests ?? []).filter(i => i !== tag) }));
  }

  const Field = ({
    icon: Icon, label, field, placeholder, multiline,
  }: {
    icon: LucideIcon; label: string; field: keyof typeof form; placeholder: string; multiline?: boolean;
  }) => (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs tracking-wide" style={{ color: 'var(--app-text-muted)' }}>{label}</label>
      <div
        className="flex items-start gap-3 rounded-xl px-3 py-2.5 border transition-colors"
        style={{ backgroundColor: 'var(--app-input-bg)', borderColor: 'var(--app-input-border)' }}
      >
        <Icon className="w-4 h-4 mt-0.5 shrink-0" style={{ color: 'var(--app-text-subtle)' }} />
        {multiline ? (
          <textarea
            value={form[field] || ''}
            onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
            placeholder={placeholder}
            rows={3}
            className="flex-1 bg-transparent text-sm outline-none resize-none"
            style={{ color: 'var(--app-text)' }}
          />
        ) : (
          <input
            type="text"
            value={form[field] || ''}
            onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
            placeholder={placeholder}
            className="flex-1 bg-transparent text-sm outline-none"
            style={{ color: 'var(--app-text)', height: '28px' }}
          />
        )}
      </div>
    </div>
  );

  return (
    <div className="absolute inset-0 z-50 flex flex-col" style={{ backgroundColor: 'var(--app-bg)' }}>
      {/* Modal Header */}
      <div
        className="app-screen-header flex items-center justify-between px-4 pt-12 pb-4 border-b shrink-0"
        style={{ borderColor: 'var(--app-divider)' }}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className="size-10 rounded-full flex items-center justify-center active:scale-90 transition-transform"
          style={{ backgroundColor: 'var(--app-surface)' }}
        >
          <X className="w-5 h-5" style={{ color: 'var(--app-text)' }} />
        </button>
        <span className="text-base" style={{ color: 'var(--app-text)' }}>Edit Profile</span>
        <button
          onClick={() => { onSave(form); onClose(); }}
          aria-label="Save"
          className="size-10 rounded-full flex items-center justify-center active:scale-90 transition-transform"
          style={{ backgroundColor: 'var(--app-accent-solid)' }}
        >
          <Check className="w-5 h-5 text-white" />
        </button>
      </div>

      {/* Avatar picker */}
      <div className="flex flex-col items-center gap-3 py-6 border-b" style={{ borderColor: 'var(--app-divider)' }}>
        <div className="relative">
          <div className="size-24 rounded-full overflow-hidden border-2" style={{ borderColor: 'var(--app-accent-solid)' }}>
            <ProfileAvatar src={user.avatar} name={user.name} alt="Profile" className="w-full h-full object-cover" />
          </div>
          <label
            htmlFor="avatar-upload-modal"
            className="absolute -bottom-1 -right-1 size-8 rounded-full flex items-center justify-center border-2 cursor-pointer"
            style={{ backgroundColor: 'var(--app-accent-solid)', borderColor: 'var(--app-bg)' }}
            aria-label="Change photo"
          >
            <Camera className="w-3.5 h-3.5 text-white" />
          </label>
          <input
            id="avatar-upload-modal"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={onAvatarChange}
          />
        </div>
        <p className="text-xs text-center px-4" style={{ color: 'var(--app-text-muted)' }}>
          Still JPEG, PNG, or WebP · Max 5 MB / 16 megapixels
        </p>
      </div>

      {/* Form */}
      <div className="flex-1 overflow-y-auto px-4 py-5 flex flex-col gap-4">
        <Field icon={User}      label="Full Name"   field="name"     placeholder="Your full name" />
        <Field icon={Briefcase} label="Job Title"   field="title"    placeholder="e.g. Senior Engineer" />
        <Field icon={Building}  label="Company"     field="company"  placeholder="Your company name" />
        <Field icon={FileText}  label="Bio"         field="bio"      placeholder="Short bio..." multiline />
        <Field icon={Linkedin}  label="LinkedIn"    field="linkedin" placeholder="linkedin.com/in/you" />

        {/* Interests chip editor */}
        <div className="flex flex-col gap-1.5">
          <label className="text-xs tracking-wide" style={{ color: 'var(--app-text-muted)' }}>Research Interests</label>
          <div
            className="flex flex-wrap gap-2 min-h-[44px] rounded-xl px-3 py-2 border"
            style={{ backgroundColor: 'var(--app-input-bg)', borderColor: 'var(--app-input-border)' }}
          >
            {(form.interests ?? []).map(tag => (
              <span
                key={tag}
                className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs"
                style={{ backgroundColor: 'var(--app-accent-dim)', color: 'var(--app-accent)' }}
              >
                {tag}
                <button onClick={() => removeInterest(tag)} className="active:opacity-70">
                  <X size={10} />
                </button>
              </span>
            ))}
            <input
              type="text"
              value={interestInput}
              onChange={e => setInterestInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ',') {
                  e.preventDefault();
                  addInterest(interestInput);
                  setInterestInput('');
                }
              }}
              onBlur={() => { if (interestInput.trim()) { addInterest(interestInput); setInterestInput(''); } }}
              placeholder={(form.interests ?? []).length === 0 ? 'Add interests…' : ''}
              className="flex-1 min-w-[120px] bg-transparent text-sm outline-none"
              style={{ color: 'var(--app-text)', height: '28px' }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Config ────────────────────────────────────────────────── */

/* ─── Main Screen ───────────────────────────────────────────── */
export default function ProfileScreen() {
  const navigate = useNavigate();
  const {
    user,
    profile,
    signOut,
    deleteAccount,
    updateProfile,
    applyUploadedAvatar,
    theme,
    toggleTheme,
    notes,
    aiPreferences,
    setAiPreference,
    currentEventId,
    isCurrentEventMember,
    authSession,
    isAuthSessionCurrent,
  } = useApp();
  const { isEnabled } = useEventModules();
  const { getFavoriteIdsByType } = useFavorites();

  const [currentEvent, setCurrentEvent] = useState<ConferenceEvent | null>(null);
  useEffect(() => {
    if (!currentEventId) { setCurrentEvent(null); return; }
    let cancelled = false;
    eventService.getById(currentEventId)
      .then((ev) => { if (!cancelled) setCurrentEvent(ev); })
      .catch(() => { /* non-fatal */ });
    return () => { cancelled = true; };
  }, [currentEventId]);

  const eventDateRange = (ev: ConferenceEvent) => {
    try {
      const s = new Date(`${ev.date_start}T00:00:00`);
      const e = new Date(`${ev.date_end}T00:00:00`);
      return `${s.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}–${e.toLocaleDateString('en-US', { day: 'numeric' })} · ${ev.location ?? ''}`.replace(/ · $/, '');
    } catch {
      return ev.location ?? '';
    }
  };

  const [showEditModal, setShowEditModal] = useState(false);
  const [showQRBadge, setShowQRBadge] = useState(false);
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);
  const [showDeleteAccountConfirm, setShowDeleteAccountConfirm] = useState(false);
  const [directoryPreference, setDirectoryPreference] = useState<DirectoryPreference | null>(null);
  const [directoryPreferenceLoading, setDirectoryPreferenceLoading] = useState(false);
  const [directoryPreferenceSaving, setDirectoryPreferenceSaving] = useState(false);
  const directoryEventRef = useRef(currentEventId);
  const [exportLoading, setExportLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  directoryEventRef.current = currentEventId;

  useEffect(() => {
    setDirectoryPreference(null);
    setDirectoryPreferenceSaving(false);
    if (!currentEventId || !isCurrentEventMember) {
      setDirectoryPreferenceLoading(false);
      return;
    }
    const owner = authSession;
    if (!owner || !isAuthSessionCurrent(owner)) {
      setDirectoryPreferenceLoading(false);
      return;
    }
    const eventId = currentEventId;
    let cancelled = false;
    setDirectoryPreferenceLoading(true);
    directoryPrivacyService.getDirectoryPreference(eventId, { signal: owner.signal })
      .then((preference) => {
        if (
          !cancelled
          && isAuthSessionCurrent(owner)
          && directoryEventRef.current === eventId
        ) {
          setDirectoryPreference(preference);
        }
      })
      .catch(() => {
        if (!cancelled && isAuthSessionCurrent(owner)) {
          notify.error('Could not load directory setting');
        }
      })
      .finally(() => {
        if (
          !cancelled
          && isAuthSessionCurrent(owner)
          && directoryEventRef.current === eventId
        ) {
          setDirectoryPreferenceLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [authSession, currentEventId, isAuthSessionCurrent, isCurrentEventMember]);

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset immediately so choosing the same file after a failed upload works.
    e.target.value = '';
    const session = authSession;
    if (!file || !session || !isAuthSessionCurrent(session)) return;
    if (file.size > 5 * 1024 * 1024) {
      notify.error('Upload failed', 'Image too large (max 5 MB)');
      return;
    }
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { url } = await apiClient.uploadFile<{ url: string }>(
        '/files/upload-avatar',
        fd,
        { signal: session.signal },
      );
      if (!isAuthSessionCurrent(session)) return;
      applyUploadedAvatar(url, session);
      if (!isAuthSessionCurrent(session)) return;
      notify.success('Photo updated');
    } catch (err: unknown) {
      if (isAuthSessionCurrent(session)) {
        notify.error('Upload failed', err instanceof Error ? err.message : 'Unknown error');
      }
    }
    e.target.value = '';
  };

  const handleDownloadData = async () => {
    const session = authSession;
    if (!session || !isAuthSessionCurrent(session)) return;
    setExportLoading(true);
    try {
      const date = new Date().toISOString().slice(0, 10);
      const data = await apiClient.get<unknown>('/users/me/export', { signal: session.signal });
      if (!isAuthSessionCurrent(session)) return;
      const result = await exportAccountFile({
        filename: `my_data_${date}.json`,
        content: JSON.stringify(data, null, 2),
      });
      if (!isAuthSessionCurrent(session)) return;
      if (result === 'cancelled') return;
      if (result === 'shared') {
        notify.info('Export sent', 'Check the destination you selected');
      } else {
        notify.success('Download started');
      }
    } catch (err: unknown) {
      if (isAuthSessionCurrent(session)) {
        notify.error('Export failed', err instanceof Error ? err.message : 'Unknown error');
      }
    } finally {
      if (isAuthSessionCurrent(session)) setExportLoading(false);
    }
  };

  const handleDirectoryToggle = async () => {
    const session = authSession;
    const eventId = currentEventId;
    if (
      !session
      || !eventId
      || !directoryPreference
      || directoryPreferenceSaving
      || !isAuthSessionCurrent(session)
    ) return;
    const next = !directoryPreference.show_in_directory;
    setDirectoryPreferenceSaving(true);
    try {
      const preference = await directoryPrivacyService.setDirectoryPreference(
        eventId,
        next,
        { signal: session.signal },
      );
      if (
        !isAuthSessionCurrent(session)
        || directoryEventRef.current !== eventId
      ) return;
      setDirectoryPreference(preference);
      notify.info(
        preference.show_in_directory
          ? 'Directory visibility enabled for this event'
          : 'Directory visibility disabled for this event',
      );
    } catch {
      if (isAuthSessionCurrent(session)) {
        notify.error('Could not update directory setting');
      }
    } finally {
      if (
        isAuthSessionCurrent(session)
        && directoryEventRef.current === eventId
      ) {
        setDirectoryPreferenceSaving(false);
      }
    }
  };

  const displayUser = {
    id: profile?.id ?? user?.id ?? '',
    name: profile?.name ?? user?.name ?? 'Your Name',
    title: profile?.title ?? user?.title ?? '',
    company: profile?.company ?? user?.company ?? '',
    bio: profile?.bio ?? user?.bio ?? '',
    linkedin: profile?.linkedin ?? user?.linkedin ?? '',
    email: profile?.email ?? user?.email ?? '',
    avatar: profile?.avatar_url ?? user?.avatar ?? '',
    interests: profile?.interests ?? [],
  };

  const handleLogout = async () => {
    await signOut();
    notify.success('Signed out successfully');
    navigate('/signin', { replace: true });
  };

  const handleDeleteAccount = async (confirmEmail: string) => {
    const session = authSession;
    if (!session || !isAuthSessionCurrent(session)) return;
    await deleteAccount(confirmEmail, session);
    notify.success('Account deleted', 'Your account and personal data have been removed.');
    navigate('/signin', { replace: true });
  };

  const handleSupportFeedback = () => {
    const opened = openSupportEmail({
      appVersion: APP_VERSION,
      accountEmail: displayUser.email,
    });

    if (!opened) {
      notify.error(
        'Could not open your email app',
        `Email us at ${SUPPORT_EMAIL}.`,
      );
    }
  };

  const handleCopySupportEmail = async () => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
      await navigator.clipboard.writeText(SUPPORT_EMAIL);
      notify.success('Support email copied');
    } catch {
      notify.error('Could not copy support email', 'Select the address above to copy it');
    }
  };

  const handleSaveProfile = async (data: Partial<typeof displayUser>) => {
    const session = authSession;
    if (!session || !isAuthSessionCurrent(session)) return;
    const { error } = await updateProfile({
      name: data.name,
      title: data.title,
      company: data.company,
      bio: data.bio,
      linkedin: data.linkedin,
      interests: data.interests,
    }, session);
    if (!isAuthSessionCurrent(session)) return;
    if (error) {
      notify.error('Could not update profile', error.message);
    } else {
      notify.success('Profile updated');
    }
  };

  // Real stats from favorites + notes
  const savedSessions = getFavoriteIdsByType('session').length;
  const savedPapers   = getFavoriteIdsByType('paper').length;
  const stats = [
    { label: 'Sessions', value: String(savedSessions), icon: Calendar, route: '/library/agenda' },
    { label: 'Papers',   value: String(savedPapers),   icon: BookOpen,  route: '/library/papers' },
    { label: 'Notes',    value: String(notes.length),  icon: FileText,  route: '/notes' },
  ];

  const cardStyle = {
    backgroundColor: 'var(--app-surface)',
    borderColor: 'var(--app-border)',
    boxShadow: 'var(--app-card-shadow)',
  };

  return (
    <div className="size-full flex flex-col overflow-hidden transition-colors duration-300" style={{ backgroundColor: 'var(--app-bg)' }}>

      {/* Edit Profile Modal */}
      {showEditModal && (
        <EditProfileModal
          user={displayUser}
          onSave={handleSaveProfile}
          onClose={() => setShowEditModal(false)}
          onAvatarChange={handleAvatarUpload}
        />
      )}

      {/* QR Badge Modal */}
      {showQRBadge && (
        <QRBadgeModal user={displayUser} onClose={() => setShowQRBadge(false)} />
      )}

      {/* Sign Out Confirmation */}
      {showSignOutConfirm && (
        <SignOutConfirm
          onConfirm={handleLogout}
          onCancel={() => setShowSignOutConfirm(false)}
        />
      )}

      {/* Header */}
      <div
        className="app-screen-header flex items-center justify-between px-4 pt-12 pb-4 border-b shrink-0"
        style={{ backgroundColor: 'var(--app-bg)', borderColor: 'var(--app-divider)' }}
      >
        <button
          onClick={() => navigate(-1)}
          aria-label="Go back"
          className="size-10 rounded-full flex items-center justify-center active:scale-90 transition-transform"
          style={{ backgroundColor: 'var(--app-surface)' }}
        >
          <ArrowLeft className="w-5 h-5" style={{ color: 'var(--app-text)' }} />
        </button>
        <span className="text-base" style={{ color: 'var(--app-text)' }}>My Profile</span>
        <button
          onClick={() => setShowEditModal(true)}
          aria-label="Edit profile"
          className="size-10 rounded-full flex items-center justify-center active:scale-90 transition-transform"
          style={{ backgroundColor: 'var(--app-surface)' }}
        >
          <Edit3 className="w-4 h-4" style={{ color: 'var(--app-accent)' }} />
        </button>
      </div>

      {/* Scrollable Content */}
      <div className="app-nav-content flex-1 overflow-y-auto">

        {/* Avatar & Info */}
        <div className="flex flex-col items-center px-4 py-8">
          <div className="relative mb-4">
            <div
              className="absolute -inset-2 rounded-full blur-xl opacity-40"
              style={{ backgroundColor: 'var(--app-accent-solid)' }}
            />
            <div
              className="relative size-24 rounded-full overflow-hidden border-2"
              style={{ borderColor: 'var(--app-accent-solid)' }}
            >
              <ProfileAvatar src={displayUser.avatar} name={displayUser.name} alt="Profile" className="w-full h-full object-cover" />
            </div>
            <div
              className="absolute -bottom-1 -right-1 size-7 rounded-full border-2 flex items-center justify-center"
              style={{ backgroundColor: 'var(--app-accent-solid)', borderColor: 'var(--app-bg)' }}
            >
              <Star className="w-3.5 h-3.5 text-white fill-white" />
            </div>
          </div>
          <h1 className="text-xl mt-1" style={{ color: 'var(--app-text)' }}>{displayUser.name}</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--app-text-muted)' }}>{displayUser.title}</p>
          <p className="text-xs mt-0.5" style={{ color: 'var(--app-text-subtle)' }}>{displayUser.company}</p>
          {displayUser.bio && (
            <p className="text-xs mt-2 text-center px-6 leading-relaxed" style={{ color: 'var(--app-text-muted)' }}>
              {displayUser.bio}
            </p>
          )}
          {/* Social links */}
          {displayUser.linkedin && (
            <div className="flex gap-3 mt-3">
              <span className="text-[10px] px-3 py-1 rounded-full" style={{ backgroundColor: 'var(--app-tag-bg)', color: 'var(--app-tag-text)' }}>
                LinkedIn
              </span>
            </div>
          )}
        </div>

        {/* Stats Row — tappable */}
        <div className="flex gap-3 px-4 mb-5">
          {stats.map(({ label, value, icon: Icon, route }) => (
            <button
              key={label}
              onClick={() => navigate(route)}
              className="flex-1 rounded-xl p-3 border flex flex-col items-center gap-1.5 active:scale-[0.96] transition-transform"
              style={cardStyle}
            >
              <Icon className="w-4 h-4" style={{ color: 'var(--app-accent)' }} />
              <span className="text-lg" style={{ color: 'var(--app-text)' }}>{value}</span>
              <span className="text-xs" style={{ color: 'var(--app-text-muted)' }}>{label}</span>
            </button>
          ))}
        </div>

        {/* My Badge — QR Code */}
        <div className="px-4 mb-5">
          <SectionLabel label="My Badge" />
          <button
            onClick={() => setShowQRBadge(true)}
            className="w-full rounded-xl p-4 border text-left active:scale-[0.98] transition-transform flex items-center gap-3"
            style={{
              background: `linear-gradient(135deg, var(--app-accent-dim) 0%, transparent 100%)`,
              borderColor: 'var(--app-accent-border)',
              boxShadow: 'var(--app-card-shadow)',
            }}
          >
            <div
              className="size-12 rounded-xl flex items-center justify-center shrink-0"
              style={{ backgroundColor: 'var(--app-accent-dim)' }}
            >
              <QrCode className="w-6 h-6" style={{ color: 'var(--app-accent)' }} />
            </div>
            <div className="flex-1">
              <p className="text-sm" style={{ color: 'var(--app-text)' }}>Conference QR Badge</p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--app-text-muted)' }}>
                Tap to show for check-in & networking
              </p>
            </div>
            <ChevronRight className="w-4 h-4 shrink-0" style={{ color: 'var(--app-text-subtle)' }} />
          </button>
        </div>

        {/* Current Event */}
        <div className="px-4 mb-5">
          <SectionLabel label="My Event" />
          <button
            onClick={() => navigate('/my-conferences')}
            className="w-full rounded-xl p-4 border text-left active:scale-[0.98] transition-transform flex items-center justify-between"
            style={{
              background: `linear-gradient(135deg, var(--app-accent-dim) 0%, transparent 100%)`,
              borderColor: 'var(--app-accent-border)',
              boxShadow: 'var(--app-card-shadow)',
            }}
          >
            {currentEvent ? (
              <div>
                <p className="text-sm" style={{ color: 'var(--app-text)' }}>{currentEvent.name}</p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--app-text-muted)' }}>{eventDateRange(currentEvent)}</p>
                <span
                  className="inline-block mt-2 text-white text-[10px] px-2.5 py-1 rounded-full tracking-wide"
                  style={{ backgroundColor: isCurrentEventMember ? 'var(--app-accent-solid)' : 'var(--app-text-subtle)' }}
                >
                  {isCurrentEventMember ? 'REGISTERED' : 'VIEWING'}
                </span>
              </div>
            ) : (
              <div>
                <p className="text-sm" style={{ color: 'var(--app-text)' }}>No event selected</p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--app-text-muted)' }}>Tap to browse your conferences</p>
              </div>
            )}
            <ChevronRight className="w-4 h-4 shrink-0" style={{ color: 'var(--app-text-subtle)' }} />
          </button>
        </div>

        {/* ── AI Preferences ── */}
        <div className="px-4 mb-2">
          <SectionLabel label="AI Copilot" />
          <div className="rounded-xl border overflow-hidden" style={cardStyle}>
            <SettingRow icon={Brain} label="Rubikoo AI settings" subtitle="Enable AI, review providers and choose what to share" onPress={() => {
              void manageAISharing(authSession?.signal).catch(() => notify.error('Could not load AI sharing settings'));
            }} />
            {isEnabled('ai_copilot') && <>
            <Divider />
            <SettingRow
              icon={Sparkles}
              label="AI Suggestions"
              subtitle={aiPreferences.aiSuggestions ? 'Smart recommendations enabled' : 'Recommendations paused'}
              isToggle
              value={aiPreferences.aiSuggestions}
              onPress={() => {
                setAiPreference('aiSuggestions', !aiPreferences.aiSuggestions);
                notify.info(aiPreferences.aiSuggestions ? 'AI suggestions paused' : 'AI suggestions enabled');
              }}
            />
            <Divider />
            <SettingRow
              icon={Brain}
              label="Auto-Generate Insights"
              subtitle="AI insights on papers & sessions"
              isToggle
              value={aiPreferences.aiAutoInsights}
              onPress={() => {
                setAiPreference('aiAutoInsights', !aiPreferences.aiAutoInsights);
                notify.info(aiPreferences.aiAutoInsights ? 'Auto-insights disabled' : 'Auto-insights enabled');
              }}
            />
            {isEnabled('notes') && (<>
              <Divider />
              <SettingRow
                icon={MessageSquare}
                label="Chat History"
                subtitle={`${notes.filter(n => n.source.startsWith('ai-')).length} AI-generated notes saved`}
                onPress={() => navigate('/notes')}
              />
            </>)}
            </>}
          </div>
        </div>
        {/* ── Appearance ── */}
        <div className="px-4 mb-2">
          <SectionLabel label="Appearance" />
          <div className="rounded-xl border overflow-hidden" style={cardStyle}>
            {/* Theme Toggle — custom look */}
            <button
              onClick={toggleTheme}
              className="w-full flex items-center gap-3 px-4 py-3.5 transition-colors active:opacity-70"
            >
              <div
                className="size-9 rounded-xl flex items-center justify-center shrink-0"
                style={{ backgroundColor: 'var(--app-accent-dim)' }}
              >
                {theme === 'dark'
                  ? <Moon className="w-4 h-4" style={{ color: 'var(--app-accent)' }} />
                  : <Sun className="w-4 h-4" style={{ color: 'var(--app-accent)' }} />
                }
              </div>
              <div className="flex-1 text-left">
                <p className="text-sm" style={{ color: 'var(--app-text)' }}>
                  {theme === 'dark' ? 'Dark Mode' : 'Light Mode'}
                </p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--app-text-muted)' }}>
                  {theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
                </p>
              </div>
              {/* Pill toggle showing sun/moon */}
              <div
                className="flex items-center gap-1 rounded-full p-1 border transition-colors duration-200"
                style={{ backgroundColor: 'var(--app-muted)', borderColor: 'var(--app-border)' }}
              >
                <div
                  className="size-7 rounded-full flex items-center justify-center transition-all duration-200"
                  style={theme === 'dark'
                    ? { backgroundColor: 'transparent' }
                    : { backgroundColor: 'var(--app-accent-solid)' }
                  }
                >
                  <Sun className="w-3.5 h-3.5" style={{ color: theme === 'light' ? '#fff' : 'var(--app-text-subtle)' }} />
                </div>
                <div
                  className="size-7 rounded-full flex items-center justify-center transition-all duration-200"
                  style={theme === 'dark'
                    ? { backgroundColor: 'var(--app-accent-solid)' }
                    : { backgroundColor: 'transparent' }
                  }
                >
                  <Moon className="w-3.5 h-3.5" style={{ color: theme === 'dark' ? '#fff' : 'var(--app-text-subtle)' }} />
                </div>
              </div>
            </button>
          </div>
        </div>

        {/* ── Notifications ── */}
        <div className="px-4 mb-2">
          <SectionLabel label="Notifications" />
          <div className="rounded-xl border overflow-hidden" style={cardStyle}>
            <SettingRow
              icon={Bell}
              label="Push Notifications"
              subtitle="Manage permission in your device settings"
              onPress={() => notify.info('Manage push notification permission in your device settings.')}
            />
            {isEnabled('agenda') && (<>
              <Divider />
              <SettingRow
                icon={Calendar}
                label="Session Reminders"
                subtitle="Set reminders from your saved agenda"
                onPress={() => navigate('/agenda')}
              />
            </>)}
          </div>
        </div>

        {/* ── Account ── */}
        <div className="px-4 mb-2">
          <SectionLabel label="Account" />
          <div className="rounded-xl border overflow-hidden" style={cardStyle}>
            {/* Email — read-only display */}
            <div className="w-full flex items-center gap-3 px-4 py-3.5">
              <div className="size-9 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: 'var(--app-accent-dim)' }}>
                <AtSign className="w-4 h-4" style={{ color: 'var(--app-accent)' }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm" style={{ color: 'var(--app-text)' }}>Email</p>
                <p className="text-xs mt-0.5 truncate" style={{ color: 'var(--app-text-muted)' }}>{displayUser.email}</p>
              </div>
            </div>
            <Divider />
            <SettingRow
              icon={KeyRound}
              label="Change Password"
              subtitle="Update the password for your account"
              onPress={() => navigate('/change-password')}
            />
            {currentEventId && isCurrentEventMember && (<>
              <Divider />
              <SettingRow
                icon={directoryPreference?.show_in_directory ? Eye : EyeOff}
                label="Show me in this event’s attendee directory"
                subtitle={directoryPreferenceLoading
                  ? 'Loading directory preference…'
                  : directoryPreference?.moderator_hidden
                    ? 'You opted in, but an event moderator has hidden your directory profile.'
                    : directoryPreference?.show_in_directory
                      ? 'You appear in this event’s attendee directory'
                      : 'You are hidden from this event’s attendee directory'}
                isToggle
                value={directoryPreference?.show_in_directory ?? false}
                disabled={directoryPreferenceLoading || directoryPreferenceSaving || !directoryPreference}
                onPress={handleDirectoryToggle}
              />
            </>)}
            <Divider />
            <SettingRow
              icon={Download}
              label={exportLoading ? 'Preparing export…' : 'Download My Data'}
              subtitle="Export your profile, notes & favorites"
              onPress={exportLoading ? () => {} : handleDownloadData}
            />
            <Divider />
            <SettingRow
              icon={FileText}
              label="Account Deletion Information"
              subtitle="Read the public deletion guide"
              color="#ef4444"
              onPress={() => navigate('/account-deletion')}
            />
            <Divider />
            <DeleteAccountConfirm
              open={showDeleteAccountConfirm}
              email={displayUser.email}
              onConfirm={handleDeleteAccount}
              onOpenChange={setShowDeleteAccountConfirm}
            />
          </div>
        </div>

        {/* ── Support ── */}
        <div className="px-4 mb-5">
          <SectionLabel label="Support" />
          <div className="rounded-xl border overflow-hidden" style={cardStyle}>
            <SettingRow
              icon={MessageSquare}
              label="Support & Feedback"
              subtitle="Questions, issues, or ideas"
              onPress={handleSupportFeedback}
            />
            <div className="px-4 pb-4 text-sm text-muted-foreground flex items-center justify-between gap-2">
              <span>
                Email us at{' '}
                <a
                  className="underline select-text"
                  href={`mailto:${SUPPORT_EMAIL}`}
                  style={{ color: 'var(--app-accent)' }}
                >
                  {SUPPORT_EMAIL}
                </a>
              </span>
              <button
                type="button"
                aria-label="Copy support email"
                className="shrink-0 rounded-md border px-2 py-1 text-xs"
                onClick={handleCopySupportEmail}
                style={{ color: 'var(--app-accent)', borderColor: 'var(--app-accent-border)' }}
              >
                Copy
              </button>
            </div>
            <Divider />
            <SettingRow
              icon={Info}
              label="About"
              subtitle={`v${APP_VERSION} · AI Conference App`}
              onPress={() => notify.info(`AI Conference App v${APP_VERSION}\nBuilt with React + TypeScript`)}
            />
            <Divider />
            <SettingRow
              icon={Scale}
              label="Terms & Privacy Policy"
              onPress={() => navigate('/legal')}
            />
          </div>
        </div>

        {/* Sign Out */}
        <div className="px-4">
          <button
            onClick={() => setShowSignOutConfirm(true)}
            className="w-full h-12 rounded-xl flex items-center justify-center gap-2 text-sm active:scale-[0.98] transition-transform border"
            style={{
              color: 'var(--app-danger)',
              backgroundColor: 'var(--app-danger-dim)',
              borderColor: 'var(--app-danger-border)',
            }}
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
        </div>

        <p className="text-center text-xs mt-8 mb-2" style={{ color: 'var(--app-text-subtle)' }}>
          AI Conference App · v1.0.0
        </p>
      </div>

      <BottomNav />
    </div>
  );
}
